/**
 * Teacher → Results & Analytics
 *
 * Roll-up of teacher's subjects: per-subject averages, pass rates,
 * grade-distribution histogram across all of their marks.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, TrendingUp, Loader2, Award } from 'lucide-react';
import { portalApi } from './teacherApi';

const GRADE_ORDER = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'E', 'F', '?'];

export const TeacherResults = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['teacher-results'],
    queryFn: () => portalApi('/teacher/results'),
  });

  if (isLoading)
    return <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading…</div>;
  if (!data || (data.subjects.length === 0 && !data.marks_total))
    return <div className="text-center text-slate-500 p-8 border border-dashed rounded">No results yet — once marks are entered for any of your subjects, the roll-ups appear here.</div>;

  // Order grade distribution
  const dist = data.grade_distribution || {};
  const gradeRows = GRADE_ORDER.filter(g => dist[g]).map(g => ({ grade: g, count: dist[g] }));
  const maxCount = gradeRows.reduce((m, r) => Math.max(m, r.count), 1);

  return (
    <div className="space-y-3" data-testid="teacher-results-page">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-indigo-500" /> Results &amp; Analytics
      </h2>

      {/* Topline */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Total marks recorded</p>
          <p className="text-2xl font-bold">{data.marks_total ?? 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Overall pass rate</p>
          <p className="text-2xl font-bold">{data.pass_rate_pct != null ? `${data.pass_rate_pct}%` : '—'}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Subjects tracked</p>
          <p className="text-2xl font-bold">{data.subjects.length}</p>
        </CardContent></Card>
      </div>

      {/* Per-subject table */}
      <Card>
        <CardContent className="pt-4">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><TrendingUp className="h-4 w-4" /> Per subject</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 border-b">
              <tr>
                <th className="text-left p-1.5">Subject</th>
                <th className="text-right p-1.5">Marks</th>
                <th className="text-right p-1.5">Average</th>
                <th className="text-right p-1.5">Pass rate</th>
              </tr>
            </thead>
            <tbody>
              {data.subjects.map((s) => (
                <tr key={s.subject_id} className="border-b last:border-0" data-testid={`teacher-results-row-${s.subject_id}`}>
                  <td className="p-1.5 font-medium">{s.subject_name}</td>
                  <td className="p-1.5 text-right">{s.count}</td>
                  <td className="p-1.5 text-right">{s.average != null ? `${s.average}%` : '—'}</td>
                  <td className="p-1.5 text-right">
                    {s.pass_rate != null
                      ? <Badge variant={s.pass_rate >= 60 ? 'default' : 'destructive'}>{s.pass_rate}%</Badge>
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Grade histogram */}
      {gradeRows.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><Award className="h-4 w-4" /> Grade distribution</h3>
            <div className="space-y-1.5">
              {gradeRows.map((g) => (
                <div key={g.grade} className="flex items-center gap-2">
                  <div className="w-10 text-xs font-medium text-slate-600">{g.grade}</div>
                  <div className="flex-1 bg-slate-100 rounded h-5 overflow-hidden">
                    <div className="bg-indigo-500 h-full transition-all"
                          style={{ width: `${(g.count / maxCount) * 100}%` }} />
                  </div>
                  <div className="w-10 text-right text-xs text-slate-600">{g.count}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TeacherResults;
